import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Badge,
  Alert,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import {
  FaCertificate,
  FaEye,
  FaDownload,
  FaTrash,
  FaSyncAlt,
  FaPlusCircle,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CertificateService from "../../../../services/certificateService";
import IssueCertificateModal from "../../../../components/admin/IssueCertificateModal";

const BatchCertificatesTab = ({
  batchId,
  batchName,
  students,
  onCertificateIssued,
}) => {
  const [batchData, setBatchData] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  useEffect(() => {
    loadAllStudentsData();
  }, [batchId]);

  const loadAllStudentsData = async () => {
    try {
      setLoading(true);
      console.log("Calling API for batch ID:", batchId);
      const response =
        await CertificateService.getBatchCertificateStatus(batchId);
      console.log("Full API Response:", response);

      // Handle different response structures
      let responseData = response.data;

      // If response.data has a data property (nested)
      if (response.data && response.data.data) {
        responseData = response.data.data;
      }

      console.log("Processed response data:", responseData);

      setBatchData(responseData.batch || null);
      setAllStudents(responseData.students || []);
      console.log("Students count:", responseData.students?.length);
    } catch (error) {
      console.error("Failed to load certificate data:", error);
      toast.error("Failed to load certificate data");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      await loadAllStudentsData();
      toast.success("Data refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleIssueClick = (student) => {
    console.log("Student clicked for certificate:", student);
    // Ensure student has id property
    const studentWithId = {
      id: student.student_id,
      name: student.student_name,
      email: student.email,
      phone: student.phone,
    };
    setSelectedStudent(studentWithId);
    setShowIssueModal(true);
  };

  const handleIssueSuccess = () => {
    setShowIssueModal(false);
    loadAllStudentsData();
    if (onCertificateIssued) onCertificateIssued();
    toast.success("Certificate issued successfully");
  };

  const handleViewCertificate = async (certificateId) => {
    try {
      await CertificateService.viewCertificate(certificateId);
    } catch (error) {
      toast.error("Failed to view certificate");
    }
  };

  const handleDownloadCertificate = async (certificateId) => {
    try {
      await CertificateService.downloadCertificate(certificateId);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download certificate");
    }
  };

  const handleDeleteCertificate = async () => {
    if (!selectedCertificate) return;
    try {
      await CertificateService.deleteCertificate(selectedCertificate.id);
      toast.success("Certificate deleted successfully");
      loadAllStudentsData();
      setShowDeleteModal(false);
      setSelectedCertificate(null);
    } catch (error) {
      toast.error("Failed to delete certificate");
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return "success";
    if (progress >= 50) return "info";
    if (progress >= 25) return "warning";
    return "danger";
  };

  // Statistics
  const totalStudents = allStudents.length;
  const issuedCount = allStudents.filter(
    (s) => s.certificate_status === "issued",
  ).length;
  const notIssuedCount = totalStudents - issuedCount;
  const completedCount = allStudents.filter(
    (s) => s.progress >= 100 || s.batch_status === "completed",
  ).length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading certificate data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Refresh Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Certificates Management</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={refreshData}
          disabled={refreshing}
        >
          <FaSyncAlt className={`me-2 ${refreshing ? "fa-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h6>Total Students</h6>
              <h2>{totalStudents}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h6>Certificates Issued</h6>
              <h2>{issuedCount}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-white">
            <div className="card-body">
              <h6>Not Issued</h6>
              <h2>{notIssuedCount}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h6>Completed (100%)</h6>
              <h2>{completedCount}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* All Students Table */}
      <div>
        <h6 className="mb-3">All Batch Students</h6>
        {allStudents.length === 0 ? (
          <Alert variant="info">No students found in this batch.</Alert>
        ) : (
          <Table striped hover responsive>
            <thead className="bg-light">
              <tr>
                <th>#</th>
                <th>Student Name</th>
                <th>Phone</th>
                <th>Progress</th>
                <th>Certificate Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {allStudents.map((student, index) => (
                <tr key={student.student_id}>
                  <td>{index + 1}</td>
                  <td>
                    <strong>{student.student_name}</strong>
                    <br />
                    <small className="text-muted">{student.email}</small>
                  </td>
                  <td>{student.phone || "N/A"}</td>
                  <td style={{ minWidth: "150px" }}>
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 me-2">
                        <ProgressBar
                          now={student.progress || 0}
                          variant={getProgressColor(student.progress || 0)}
                          style={{ height: "6px" }}
                        />
                      </div>
                      <small className="fw-bold">
                        {student.progress || 0}%
                      </small>
                    </div>
                  </td>
                  <td>
                    {student.certificate_status === "issued" ? (
                      <Badge
                        bg="success"
                        className="d-inline-flex align-items-center gap-1"
                      >
                        <FaCheckCircle size={10} /> Issued
                      </Badge>
                    ) : (
                      <Badge
                        bg="secondary"
                        className="d-inline-flex align-items-center gap-1"
                      >
                        <FaTimesCircle size={10} /> Not Issued
                      </Badge>
                    )}
                    {student.certificate_status === "issued" &&
                      student.certificate_number && (
                        <div className="mt-1">
                          <small className="text-muted">
                            {student.certificate_number}
                          </small>
                        </div>
                      )}
                  </td>
                  <td>
                    {student.certificate_status === "issued" ? (
                      <div className="d-flex gap-1">
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() =>
                            handleViewCertificate(student.certificate_id)
                          }
                          title="View Certificate"
                        >
                          <FaEye />
                        </Button>
                        {/* <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            handleDownloadCertificate(student.certificate_id)
                          }
                          title="Download Certificate"
                        >
                          <FaDownload />
                        </Button> */}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setSelectedCertificate({
                              id: student.certificate_id,
                              student_name: student.student_name,
                              certificate_number: student.certificate_number,
                            });
                            setShowDeleteModal(true);
                          }}
                          title="Delete Certificate"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleIssueClick(student)}
                        title="Issue Certificate"
                      >
                        <FaPlusCircle className="me-1" /> Issue
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Issue Certificate Modal */}
      <IssueCertificateModal
        show={showIssueModal}
        onHide={() => setShowIssueModal(false)}
        student={selectedStudent}
        batchId={batchId}
        batchName={batchName}
        onSuccess={handleIssueSuccess}
      />

      {/* Delete Certificate Modal */}
      <div
        className="modal show"
        style={{ display: "none" }}
        aria-hidden="true"
      >
        {/* This is a placeholder - actual modal will be rendered conditionally */}
      </div>
      {showDeleteModal && selectedCertificate && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaTrash className="me-2" />
                  Delete Certificate
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete certificate?</p>
                <p className="fw-bold">
                  {selectedCertificate.certificate_number}
                </p>
                <p>
                  for <strong>{selectedCertificate.student_name}</strong>?
                </p>
                <Alert variant="warning">
                  This action cannot be undone. The certificate will be
                  permanently deleted.
                </Alert>
              </div>
              <div className="modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteCertificate}>
                  Delete Certificate
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchCertificatesTab;
